import { ChangeEvent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  ButtonSize,
  ButtonStyles,
  ButtonTypes,
  Card,
  DetailCardProgress,
  FAIcon,
  HeaderStyle,
  IconStyle,
  ImageSlider,
  Modal,
  TableContainer,
  Tag,
  TagStyle,
  Theme,
  Tooltip,
  TooltipPreference,
  useBreakpoint,
} from '../../../../ui/components';
import {
  dateTimeToCustomFormat,
  LoggerService,
  PAGES,
  formatDateDMYYYY,
  trackPage,
  processBeforeAfterImagesData,
  processBeforeAfterImagesResponse,
  processProfilePictureData,
  convertGMT8date,
} from '../../../../ui/utils';
import { customEventPropType } from '../../../../ui/components/ImageEditor/types/propsType';
import ImageEditor from '../../../../ui/components/ImageEditor/ImageEditor';
import personalizeImg from '../../../../assets/image/personalize.png';
import intermediateMeasurementService from '../../../../sdk/services/intermediateMeasurementService';
import PhotoUnavailable from '../../../../assets/image/sample/photoUnavailable.png';
import { getRoundedNumber, getRoundedNumber2 } from '../../../../ui/utils/common';
import { decryptString } from '../../../../ui/utils/cryptoUtils';
import ParticipantService from '../../../../sdk/services/participantService';
import type { DynamicObjectInterface, AttachmentImageBA } from '../../../../sdk/type';
import healthGoalService from '../../../../sdk/services/healthGoalService';
import { UserAnswerDataType } from '../../[campaignId]';
import ChangeButton from '../../../../../src/assets/image/icons/change_button.svg';
import RemoveButton from '../../../../../src/assets/image/icons/remove_button.svg';
import { useAPICall } from '../../../../hooks';
import AttachmentService from '../../../../sdk/services/attachmentService';
import '../../../../sdk/services/axiosInterceptor';
import styles from './index.module.scss';

export interface ParticipantData {
  campaign: {
    campaignName: string;
    campaignStart: string;
    campaignEnd: string;
    configJson: DynamicObjectInterface;
  };
  coachID: string;
  coachName: string;
  email: string;
  isNewAbo: number;
  isTeamLeader: boolean;
  loginId: string;
  name: string;
  phone: string;
  pinLevel: number;
  point: PointData;
  purchase: {
    orderStatus?: number;
    orderNumber?: string;
    orderName?: string;
  }[];
  remark: string;
  rewards: {
    activityName?: string;
    rewardRemarks?: string;
    rewardId?: number;
    rewardCode?: string;
    rewardName?: string;
    rewardType?: string;
    isNew?: boolean;
    redeemDate?: string;
    rewardRecId: string;
    rewardStatus?: string;
    imageURL?: string;
    modifiedDate: string;
  }[];
  status: string;
  subscribe: {
    recId?: number;
    orderPoint?: number;
    orderNumber?: string;
  }[];
  subscribeCheck?: string;
  team: {
    teamName: string;
    teamStartDate: string;
    teamEndDate: string;
    teamLeaderLoginId: string;
    teamLeaderName: string;
    teamStatus?: string;
  };
  weightIn: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    machine: string;
    weightedDate: string;
    remarks: string;
    // for DetailCardProgress
    weightInDate: string;
    otherIndicators: DynamicObjectInterface;
  };
  weightOut: {
    acknowledgeBy?: string;
    acknowledgeByName?: string;
    acknowledgeDateTime?: string;
    machine: string;
    weightedDate: string;
    remarks: string;
    // for DetailCardProgress
    weightOutDate: string;
    otherIndicators: DynamicObjectInterface;
  };
  // for DetailCardProgress
  hideProfileImg: boolean;
  showFiveProgress: boolean;
  points?: {
    product: number;
    result: number;
    subscribe: number;
  };
}

export interface PointData {
  purchase?: number;
  purchaseAdj?: number;
  subscribe?: number;
  subscribeAdj?: number;
  winning?: number;
  winningAdj?: number;
  remarks: string;
}

export interface ProgressType {
  weighingDate: Date;
  checked: boolean;
}

export const TeamProfile = (props: {
  decodedData: DynamicObjectInterface;
  role: 'LEADER' | 'COACH' | 'PARTICIPANT';
}) => {
  const [urlParamValue, setUrlParamValue] = useState({
    campaignId: '',
    teamId: '',
    userId: '',
  });
  const [data, setData] = useState<ParticipantData>();
  const [activityNames, setActivityNames] = useState<(string | undefined)[]>([]);
  const [images, setImages] = useState<Record<string, AttachmentImageBA>>({
    before_front: { photoId: 0, url: '', file: null },
    after_front: { photoId: 0, url: '', file: null },
    before_side: { photoId: 0, url: '', file: null },
    after_side: { photoId: 0, url: '', file: null },
  });

  const [profileImageUrl, setProfileImageUrl] = useState<string | undefined>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [carouselType, setCarouselType] = useState('');
  const [userAnswerData, setUserAnswerData] = useState<UserAnswerDataType>();

  const apiCall = useAPICall();
  const location = useLocation();
  const { t } = useTranslation();

  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;

  useEffect(() => {
    setUrlParamValue(prev => {
      const paths = location.pathname.split('/');
      const teamsIdx = paths.includes('teams')
        ? paths.indexOf('teams')
        : paths.includes('participants')
          ? paths.indexOf('participants')
          : paths.indexOf('myTeam');
      if (teamsIdx !== -1 && teamsIdx + 1 < paths.length) {
        prev.campaignId = decodeURIComponent(paths[teamsIdx - 1]);
        prev.teamId = decodeURIComponent(paths[teamsIdx + 1]);
      }
      const params = new URLSearchParams(location.search);
      const userId = params.get('id') ?? '';
      prev.userId = userId;
      const myTeamIdx = paths.indexOf('myTeam');
      if (myTeamIdx !== -1 && myTeamIdx + 1 < paths.length) {
        prev.campaignId = decodeURIComponent(paths[myTeamIdx - 1]);
        prev.teamId = params.get('tId') ?? '';
      }
      return { ...prev };
    });
    fetchData();

    const timeout = setTimeout(() => {
      trackPage(PAGES.teamProfile);
    }, 1000);
    return () => {
      clearTimeout(timeout);
    };
  }, [location]);

  const fetchData = async () => {
    const res = await apiCall(ParticipantService.getParticipantDetail, urlParamValue, urlParamValue.userId);
    if (res.data.data) {
      let _data: ParticipantData = res.data.data;
      _data.hideProfileImg = true;
      _data.showFiveProgress = true;
      _data.weightIn.weightInDate = _data.weightIn.weightedDate;
      _data.weightOut.weightOutDate = _data.weightOut.weightedDate;
      if (props.role === 'COACH') {
        _data.points = {
          product: (_data.point.purchase ?? 0) + (_data.point.purchaseAdj ?? 0),
          result: (_data.point.winning ?? 0) + (_data.point.winningAdj ?? 0),
          subscribe: (_data.point.subscribe ?? 0) + (_data.point.subscribeAdj ?? 0),
        };
      }
      _data = {
        ..._data,
        weightIn: {
          ..._data.weightIn,
          otherIndicators: _data?.weightIn?.otherIndicators
            ? Object.entries(_data?.weightIn?.otherIndicators || {}).reduce(
                (acc, [key, value]) => {
                  acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                  return acc;
                },
                {} as Record<string, number | null>,
              )
            : null,
        },
        weightOut: {
          ..._data.weightOut,
          otherIndicators: _data?.weightOut?.otherIndicators
            ? Object.entries(_data?.weightOut?.otherIndicators || {}).reduce(
                (acc, [key, value]) => {
                  acc[key] = value ? getRoundedNumber2(parseFloat(decryptString(value.toString()))) : null;
                  return acc;
                },
                {} as Record<string, number | null>,
              )
            : null,
        },
      };

      setData(_data);

      let _activityNames = _data.rewards
        .filter((r, idx, self) => idx === self.findIndex(t => t.activityName === r.activityName))
        .map(r => r.activityName);
      setActivityNames(_activityNames);
    }
  };

  //#region HealthGoal function

  const getUserAnswer = async () => {
    try {
      const payload = {
        userId: urlParamValue.userId,
        campaignId: urlParamValue.campaignId,
      };
      const response = await healthGoalService.userAnswer(payload);
      if (response.status === 200) {
        let responseData = response.data.data;

        // Parse options if it is a string
        if (responseData && typeof responseData.options === 'string') {
          try {
            // responseData.options = JSON.parse(responseData.options);
            responseData.options = JSON.parse(responseData.options);
          } catch (error) {
            LoggerService.log('Error parsing options', error);
            responseData.options = null; // or handle it appropriately
          }
        }

        setUserAnswerData(responseData);
      }
    } catch (error) {
      LoggerService.log('Error retrieving question', error);
    }
  };

  //#endregion

  //#region UserFunction
  const RoleList = (): string => {
    // LoggerService.log(role);
    // if (role === 'PARTICIPANT') return 'Participant';
    // if (role === 'COACH') return 'Coach';
    // if (role === 'LEADER') return 'Leader';
    // return '-';

    return 'Participant';
  };

  const handleProfileFileImage = (e: customEventPropType, view: string) => {
    const file = e.files[0];
    setProfileImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setProfileImageUrl(objectUrl);
    setShowModal(false);
  };
  const handleProfileFile = (e: ChangeEvent<HTMLInputElement>, key: string) => {
    const file = e.target.files?.[0] as File | null;

    setProfileImageFile(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setProfileImageUrl(objectUrl);
    } else {
      setProfileImageUrl('');
    }
    setShowModal(false);
  };

  const handleFile = (e: ChangeEvent<HTMLInputElement>, key: string, isBefore: boolean) => {
    const file = e.target.files?.[0] as File | null;
    if (file) {
      const url: string = URL.createObjectURL(file);
      setImages(prevImages => ({
        ...prevImages,
        [key]: { ...prevImages[key], file, url },
      }));
    }
  };

  const deleteProfile = async () => {
    const payload = {
      loginId: urlParamValue?.userId,
    };
    const deleteProfileResponse = await apiCall(AttachmentService.removeProfile, payload);

    if (deleteProfileResponse.status === 200) {
      setProfileImageUrl('');
    }
  };

  const fetchProfileImage = async () => {
    try {
      const response = await AttachmentService.downloadProfileImage(urlParamValue?.userId ?? '');
      if (response.data && response.data.data && response.data.data.image) {
        setProfileImageUrl(response.data.data.image);
      }
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  const handleProfileUpload = async () => {
    try {
      if (profileImageFile && urlParamValue?.userId) {
        const formData = processProfilePictureData(profileImageFile, urlParamValue?.userId);
        const response = await apiCall(() => AttachmentService.postUploadProfile(formData));
        if (response.status === 200) {
          LoggerService.log('Successfully upload image', response);
        }
      }
    } catch (error) {
      LoggerService.log('Error upload image', error);
    }
  };

  const showModalFn = () => {
    setShowModal(true);
  };
  //#endregion

  useEffect(() => {
    getUserAnswer();
  }, []);

  useEffect(() => {
    fetchProfileImage();
  }, []);

  useEffect(() => {
    if (profileImageFile !== null) {
      handleProfileUpload();
    }
  }, [profileImageFile]);

  const uploadImageModal = (
    <>
      {showModal && (
        <Modal
          primaryButtonLabel={''}
          headline={t('dashboard.my-team.participant-profile.label.uploadProfilePicture')}
          containerWidth={'600px'}
          containerHeight={'600px'}
          closeIcon={true}
          onClose={() => setShowModal(false)}
        >
          <div>
            {profileImageUrl ? (
              <div className={styles['profile-image-container']}>
                <div className={styles['image-wrapper']}>
                  <img
                    src={profileImageUrl}
                    alt={t('dashboard.my-team.participant-profile.label.beforeFrontViewImageAlt')}
                    className={styles['profile-image']}
                    style={{ borderRadius: '50%' }}
                  />
                </div>
                <div className={styles['buttons-container']}>
                  <button
                    className={styles['change-photo']}
                    onClick={() => document.getElementById('profileImageUrl')?.click()}
                  >
                    <img src={ChangeButton} alt="bg" />
                    <input
                      type="file"
                      id="profileImageUrl"
                      accept="image/*"
                      onChange={e => handleProfileFile(e, 'profileImageUrl')}
                      style={{ display: 'none' }}
                    />
                  </button>
                  <button className={styles['remove-photo']} onClick={() => deleteProfile()}>
                    <img src={RemoveButton} alt="bg" />
                  </button>
                </div>
              </div>
            ) : (
              <ImageEditor onFileUpload={e => handleProfileFileImage(e, 'file')} accept="image/*" />
            )}
          </div>
        </Modal>
      )}
    </>
  );

  const userDetails = (
    <Card containerHeight="100%" classes={styles['team-usr-profile-no-padding']}>
      {/* campaign details section */}
      <div className={styles['team-usr-profile']}>
        <div className={styles['team-usr-profile-img-content']}>
          <div className={styles['team-usr-profile-img-separator']}>
            <div className={styles['team-usr-profile-img-container']}>
              <img
                src={profileImageUrl ? profileImageUrl : personalizeImg}
                alt="personalize"
                onClick={props.decodedData.name === data?.name ? showModalFn : undefined}
                className={styles['team-usr-profile-image']}
              />
            </div>
          </div>
          {data?.isNewAbo === 1 && (
            <div
              className={`${styles['team-usr-profile-img-content-new-abo']} ${styles['team-usr-profile-img-content-tag']}`}
            >
              {t('dashboard.my-team.participant-profile.label.newAbo').toUpperCase()}
            </div>
          )}
        </div>
        <div className={styles['team-usr-profile-details-info']}>
          <div className={styles['team-usr-profile-details-info-pre-header']}>
            <div className={styles['team-usr-profile-details-info-blank']}></div>
            <div className={styles['team-usr-profile-details-info-header']}>
              {t('dashboard.my-team.participant-profile.label.teamMemberProfile')}
            </div>
          </div>
          <div className={styles['team-usr-profile-container']}>
            <div className={styles['team-usr-profile-details-info-blank']}></div>
            <div className={styles['team-usr-profile-pre-content']}>
              <div className={styles['team-usr-profile-content']}>
                <div className={styles['team-usr-profile-content-info-first']}>
                  <div className={styles['team-usr-profile-content-label']}>
                    {t('dashboard.my-team.participant-profile.label.name')}
                  </div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>{data?.name}</div>
                </div>
                <div className={styles['team-usr-profile-content-info-second']}>
                  <div className={styles['team-usr-profile-content-label']}>
                    {t('dashboard.my-team.participant-profile.label.contactNo')}
                  </div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>{data?.phone}</div>
                </div>
              </div>
              <div className={styles['team-usr-profile-content-full-width']}>
                <div className={styles['team-usr-profile-content-info-first']}>
                  <div className={styles['team-usr-profile-content-label']}>
                    {t('dashboard.my-team.participant-profile.label.aboNo')}
                  </div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>{data?.loginId}</div>
                </div>
                <div className={styles['team-usr-profile-content-info-second']}>
                  <div className={styles['team-usr-profile-content-label']}>
                    {t('dashboard.my-team.participant-profile.label.email')}
                  </div>
                  <div className={`${styles['team-usr-profile-content-tag']} ${styles['email']}`}>
                    {data?.email ?? '-'}
                  </div>
                </div>
              </div>
              {/* <div className={styles['team-usr-profile-content-full-width']}>
                <div className={styles['team-usr-profile-content-info-first']}>
                  <div className={styles['team-usr-profile-content-label']}>Campaign</div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>{data?.campaign?.campaignName}</div>
                </div>
                <div className={styles['team-usr-profile-content-info-second']}>
                  <div className={styles['team-usr-profile-content-label']}>Team Name</div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>{data?.team.teamName ?? '-'}</div>
                </div>
              </div>
              <div className={styles['team-usr-profile-content-full-width']}>
                <div className={styles['team-usr-profile-content-info-first']}>
                  <div className={styles['team-usr-profile-content-label']}>Team Period</div>
                  <div className={`${styles['team-usr-profile-content-tag']}`}>
                    {data?.team.teamStartDate ? formatDateDMYYYY(new Date(data?.team.teamStartDate)) : 'N/A'} -{' '}
                    {data?.team.teamEndDate ? formatDateDMYYYY(new Date(data?.team.teamEndDate)) : 'N/A'}
                  </div>
                </div>
              </div> */}
            </div>
          </div>
        </div>
      </div>

      <div className={styles['team-usr-profile-role']}>
        <div className={styles['team-usr-profile-role-label']}>{t('Role')}</div>
        <div className={styles['team-usr-profile-role-tag']}>{RoleList()}</div>
      </div>
      {userAnswerData !== null &&
        userAnswerData?.options &&
        data?.campaign &&
        !!data?.campaign?.configJson?.campaignIsHealthGoalSectionShown && (
          <>
            <div className={styles['progress-monitoring-horizontal-line']} />
            {/* health goal section  */}
            <div>
              <div className={styles['progress-monitoring-title-container']}>
                <div className={styles['progress-monitoring-title-container-title']}>
                  {t('dashboard.my-team.participant-profile.label.healthGoal')}
                </div>
              </div>
              <div className={styles['health-goal-section']}>
                {Array.isArray(userAnswerData?.options) &&
                  userAnswerData?.options?.map(option => (
                    <div key={option.key} className={styles['health-goal-item']}>
                      {option.title}
                    </div>
                  ))}
              </div>
            </div>
          </>
        )}
    </Card>
  );
  //#endregion

  //#region Measurement and Progress
  const [progressData, setProgressData] = useState<ProgressType[]>([]);

  const fetchMeasurementImage = async () => {
    try {
      const payload = {
        campaignId: urlParamValue?.campaignId,
        teamId: urlParamValue?.teamId,
        loginId: urlParamValue?.userId,
      };
      const response = await AttachmentService.downloadBeforeAfter(payload);

      // Validate response structure
      const imageData = response?.data?.data?.image || [];

      // Update state variables based on the downloaded image data
      const updatedImages = { ...images };

      imageData.forEach((image: { photoId: number; photoType: string; url: string }) => {
        const { photoId, photoType, url } = image;
        if (Object.prototype.hasOwnProperty.call(updatedImages, photoType)) {
          updatedImages[photoType].url = url;
          updatedImages[photoType].photoId = photoId; // Assigning index from API response
        }
      });

      setImages(updatedImages);
    } catch (error) {
      LoggerService.log('Error downloading image', error);
    }
  };

  const fetchProgressMonitoringList = async () => {
    const info = {
      loginId: data?.loginId,
      userId: urlParamValue.userId,
      campaignId: urlParamValue?.campaignId,
    };

    try {
      const result = await apiCall(intermediateMeasurementService.getIntermediateMeasurementList, info);
      setProgressData(result?.data?.data);
    } catch (error) {
      LoggerService.log('Failed to retrieve campaign list', error);
    }
  };

  const handleImageUpload = (type: string) => {
    setIsOpen(true);
    setCarouselType(type);
  };

  const handleCloseModal = () => {
    setIsOpen(false); // Close the modal
  };

  const handleFileImage = (e: customEventPropType, view: string, isBefore: boolean) => {
    const file = e.files[0];
    const imageUrlSetter = isBefore ? 'before' : 'after';
    const imageStateSetter = view === 'front' ? 'front' : 'side';

    // Check if the original filename is "image.jpg"
    const originalFileName = file.name;
    const isOriginalImageName = originalFileName === 'image.jpg';

    // Get current date and time to create a unique filename
    const currentDate = new Date();
    const timestamp = currentDate.getTime(); // Get timestamp in milliseconds
    const extension = file.name.split('.').pop(); // Get the file extension

    let newFileName = originalFileName; // Default to original filename
    if (isOriginalImageName) {
      newFileName = `${timestamp}.${extension}`; // Create new filename with timestamp
    }

    setImages((prevImages: Record<string, AttachmentImageBA>) => ({
      ...prevImages,
      [`${imageUrlSetter}_${imageStateSetter}`]: {
        photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
        url: URL.createObjectURL(file),
        file: new File([file], newFileName), // Create a new File object with the renamed file if needed
      },
    }));
  };

  const handleRemoveImage = (imageUrlSetter: string, imageStateSetter: string) => {
    setImages((prevImages: Record<string, AttachmentImageBA>) => ({
      ...prevImages,
      [`${imageUrlSetter}_${imageStateSetter}`]: {
        photoId: prevImages[`${imageUrlSetter}_${imageStateSetter}`].photoId,
        url: '',
        file: null,
      },
    }));
  };

  const handleFileUpload = async () => {
    try {
      const formData = processBeforeAfterImagesData(
        images,
        urlParamValue?.campaignId,
        urlParamValue.teamId,
        urlParamValue?.userId,
      );
      const response = await apiCall(() => AttachmentService.postUploadBeforeAfter(formData));

      if (response.status === 200) {
        const newImages = processBeforeAfterImagesResponse(response, images);
        setImages({ ...newImages });
        handleCloseModal();
        LoggerService.log('Successfully uploading image', response);
      }
    } catch (error) {
      LoggerService.log('Error uploading image', error);
    }
  };

  const getProgressMonitorColumns = (
    styles: { [key: string]: string },
    t: Function,
    cb?: { [key: string]: Function },
  ): Object[] => {
    const subKpiListDisplayInProgressMonitoring =
      data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.filter(
        (i: DynamicObjectInterface) => i.displayInProgressMonitoring === 'on',
      ) || [];

    return [
      {
        key: 'weighingDate',
        text: t('dashboard.my-progress.input.date'),
        sortable: true,
        width: isDesktopView ? 20 : 25,
        render: (object: DynamicObjectInterface) => (
          <span>{dateTimeToCustomFormat(object.weighingDate, 'DD/MM/YYYY')}</span>
        ),
      },
      ...subKpiListDisplayInProgressMonitoring.map((kpi: DynamicObjectInterface) => ({
        key: kpi.subKpiCode,
        text: `${kpi.indicatorName} (${kpi.indicatorMeasurementUnit})`,
        sortable: false,
        width: '',
        render: (object: DynamicObjectInterface) => {
          const otherIndicators = JSON.parse(object.otherIndicators);
          return (
            <span>
              {otherIndicators[kpi.subKpiCode]
                ? getRoundedNumber(parseFloat(decryptString(otherIndicators[kpi.subKpiCode])))
                : ''}
            </span>
          );
        },
      })),
    ];
  };

  useEffect(() => {
    fetchMeasurementImage();
  }, []);

  useEffect(() => {
    fetchProgressMonitoringList();
  }, [data]);

  const measurementDetail = (
    <Card
      title={t('dashboard.my-team.participant-profile.label.measurement')}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      // buttonType={ButtonType.ICON}
      // button={getButtonConfig.alp}
      // buttonLabel={getButtonLabelConfig}
    >
      {isDesktopView ? (
        <div className={styles['result-container']}>
          <div className={styles['measurement-wrap']}>
            {data?.campaign && !!data?.campaign?.configJson?.campaignIsProfileImageSectionShown && (
              <div className={styles['carousel-container']}>
                <ImageSlider
                  id="beforeImg"
                  slides={[
                    {
                      src: images.before_front.url ? images.before_front.url : PhotoUnavailable,
                      alt: t('dashboard.my-team.participant-profile.label.beforeFrontViewImageAlt'),
                    },
                    {
                      src: images.before_side.url ? images.before_side.url : PhotoUnavailable,
                      alt: t('dashboard.my-team.participant-profile.label.beforeSideViewImageAlt'),
                    },
                  ]}
                  //onClick={() => handleImageUpload('before')}
                />
                {/* <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.remark')}</div>
                  <p>{data?.weightIn?.remarks ?? '-'}</p>
                </div> */}
              </div>
            )}
            <div className={styles['result-info']}>
              <div className={styles['title']}>{t('dashboard.my-team.participant-profile.label.preAssessment')}</div>
              <div className={styles['label-container']}>
                <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.weightInDate')}</div>
                <div className={styles['semicolon']}>:</div>
                <div className={styles['tag-calendar']}>
                  {data?.weightIn?.weightedDate ? (
                    <Tag
                      theme={Theme.GRAY}
                      text={dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'DD/MM/yyyy')}
                      style={TagStyle.OUTLINE}
                    />
                  ) : (
                    '-'
                  )}
                </div>
              </div>
              {data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.map(
                (i: DynamicObjectInterface) => (
                  <div className={styles['label-container']}>
                    <div className={styles['label']}>
                      {i.indicatorName} ({i.indicatorMeasurementUnit})
                    </div>
                    <div className={styles['semicolon']}>:</div>
                    <p>{data?.weightIn?.otherIndicators?.[i.subKpiCode] ?? '-'}</p>
                  </div>
                ),
              )}
              <div className={styles['label-container']}>
                <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.remarks')}</div>
                <div className={styles['semicolon']}>:</div>
                <p>{data?.weightIn?.remarks ?? '-'}</p>
              </div>
            </div>
          </div>
          <div className={styles['measurement-wrap']}>
            {data?.campaign && !!data?.campaign?.configJson?.campaignIsProfileImageSectionShown && (
              <div className={styles['carousel-container']}>
                <ImageSlider
                  id="afterImg"
                  slides={[
                    {
                      src: images.after_front.url ? images.after_front.url : PhotoUnavailable,
                      alt: t('dashboard.my-team.participant-profile.label.afterFrontViewImageAlt'),
                    },
                    {
                      src: images.after_side.url ? images.after_side.url : PhotoUnavailable,
                      alt: t('dashboard.my-team.participant-profile.label.afterSideViewImageAlt'),
                    },
                  ]}
                  //onClick={() => handleImageUpload('after')}
                />
                {/* <div className={styles['label-container']}>
                  <div className={styles['label']}>{t('admin.participant.remark')}</div>
                  <p>{data?.weightOut?.remarks ?? '-'}</p>
                </div> */}
              </div>
            )}
            <div className={styles['result-info']}>
              <div className={styles['title']}>{t('dashboard.my-team.participant-profile.label.postAssessment')}</div>
              <div className={styles['label-container']}>
                <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.weighOutDate')}</div>
                <div className={styles['semicolon']}>:</div>
                <div className={styles['wrapper']}>
                  <div className={styles['tag-calendar']}>
                    {data?.weightOut?.weightedDate ? (
                      <Tag
                        theme={Theme.GRAY}
                        text={dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'DD/MM/yyyy')}
                        style={TagStyle.OUTLINE}
                      />
                    ) : (
                      '-'
                    )}
                  </div>
                </div>
              </div>
              {data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.map(
                (i: DynamicObjectInterface) => {
                  const weightInValue = data?.weightIn?.otherIndicators?.[i.subKpiCode];
                  const weightOutValue = data?.weightOut?.otherIndicators?.[i.subKpiCode];

                  const weightOutDiffValues =
                    weightInValue !== null &&
                    weightOutValue !== null
                      ? i?.percentageCalculation === 1
                        ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
                        : getRoundedNumber(weightOutValue - weightInValue)
                      : 0;

                  return (
                    <div key={i.subKpiCode} className={styles['label-container']}>
                      <div className={styles['label']}>
                        {i.indicatorName} ({i.indicatorMeasurementUnit})
                      </div>
                      <div className={styles['semicolon']}>:</div>
                      <div className={styles['wrapper']}>
                        <div className={styles['value']}>{data?.weightOut?.otherIndicators?.[i.subKpiCode] ?? '-'}</div>
                        {weightInValue && weightOutValue && weightOutDiffValues && data?.weightOut?.weightedDate ? (
                          <div
                            className={`${
                              i.indicatorWinningCriteria === 'gain'
                                ? styles['difference-label-alt']
                                : styles['difference-label']
                            } ${
                              weightOutDiffValues > 0
                                ? styles.positive
                                : weightOutDiffValues === 0
                                  ? styles.zero
                                  : styles.negative
                            }`}
                          >
                            ({weightOutDiffValues > 0 ? '+' : ''}
                            {weightOutDiffValues?.toFixed(1)}
                            {i?.percentageCalculation === 1 ? '%' : ''})
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                },
              )}
              <div className={styles['label-container']}>
                <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.remarks')}</div>
                <div className={styles['semicolon']}>:</div>
                <div className={styles['wrapper']}>
                  <div className={styles['value']}>{data?.weightOut?.remarks ?? '-'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles['result-container-mobile']}>
            <div></div>
            <div className={styles['result-container-mobile-wrapper']}>
              {data?.campaign && !!data?.campaign?.configJson?.campaignIsProfileImageSectionShown && (
                <div className={styles['carousel-container']}>
                  <ImageSlider
                    id="beforeImg"
                    slides={[
                      {
                        src: images.before_front.url ? images.before_front.url : PhotoUnavailable,
                        alt: t('dashboard.my-team.participant-profile.label.beforeFrontViewImageAlt'),
                      },
                      {
                        src: images.before_side.url ? images.before_side.url : PhotoUnavailable,
                        alt: t('dashboard.my-team.participant-profile.label.beforeSideViewImageAlt'),
                      },
                    ]}
                    onClick={() => handleImageUpload('before')}
                  />
                </div>
              )}
              <div className={styles['result-container-mobile-title']}>
                {t('dashboard.my-team.participant-profile.label.before')}
              </div>
            </div>
            <div></div>
            <div className={styles['result-container-mobile-wrapper']}>
              {data?.campaign && !!data?.campaign?.configJson?.campaignIsProfileImageSectionShown && (
                <div className={styles['carousel-container']}>
                  <ImageSlider
                    id="afterImg"
                    slides={[
                      {
                        src: images.after_front.url ? images.after_front.url : PhotoUnavailable,
                        alt: t('dashboard.my-team.participant-profile.label.afterFrontViewImageAlt'),
                      },
                      {
                        src: images.after_side.url ? images.after_side.url : PhotoUnavailable,
                        alt: t('dashboard.my-team.participant-profile.label.afterSideViewImageAlt'),
                      },
                    ]}
                    onClick={() => handleImageUpload('after')}
                  />
                </div>
              )}
              <div className={styles['result-container-mobile-title']}>
                {t('dashboard.my-team.participant-profile.label.after')}
              </div>
            </div>
          </div>
          <div className={styles['result-container-mobile']}>
            <div></div>
            <div className={styles['result-container-mobile-wrapper']}>
              <div className={styles['result-container-mobile-header-label']}>
                {t('dashboard.my-team.participant-profile.label.mobilePreAssessmentDate')}
              </div>
              <div className={`${styles['tag-calendar']} ${styles['result-container-mobile-date']}`}>
                {data?.weightIn?.weightedDate ? (
                  <Tag
                    theme={Theme.GRAY}
                    text={dateTimeToCustomFormat(data?.weightIn?.weightedDate ?? '', 'DD/MM/yyyy')}
                    style={TagStyle.OUTLINE}
                  />
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className={styles['vertical-line-mobile']}></div>
            <div className={styles['result-container-mobile-wrapper']}>
              <div className={styles['result-container-mobile-header-label']}>
                {t('dashboard.my-team.participant-profile.label.mobilePostAssessmentDate')}
              </div>
              <div className={styles['tag-calendar']}>
                {data?.weightOut?.weightedDate ? (
                  <Tag
                    theme={Theme.GRAY}
                    text={dateTimeToCustomFormat(data?.weightOut?.weightedDate ?? '', 'DD/MM/yyyy')}
                    style={TagStyle.OUTLINE}
                  />
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>
          <div className={styles['result-container-mobile']}>
            <div>
              {data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.map(
                (i: DynamicObjectInterface) => (
                  <div className={styles['result-container-mobile-label']}>
                    {i.indicatorName} ({i.indicatorMeasurementUnit})
                  </div>
                ),
              )}
            </div>
            <div>
              {data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.map(
                (i: DynamicObjectInterface) => (
                  <div className={styles['result-container-mobile-item']}>
                    {data?.weightIn?.otherIndicators?.[i.subKpiCode] ?? '-'}
                  </div>
                ),
              )}
            </div>
            <div className={styles['vertical-line-mobile']}></div>
            <div>
              {data?.campaign?.configJson?.activities?.participant?.rules?.[4]?.subKpiCode?.map(
                (i: DynamicObjectInterface) => {
                  const weightInValue = data?.weightIn?.otherIndicators?.[i.subKpiCode];
                  const weightOutValue = data?.weightOut?.otherIndicators?.[i.subKpiCode];

                  const weightOutDiffValues =
                    weightInValue !== null &&
                    weightOutValue !== null
                      ? i?.percentageCalculation === 1
                        ? getRoundedNumber(((weightOutValue - weightInValue) / weightInValue) * 100)
                        : getRoundedNumber(weightOutValue - weightInValue)
                      : 0;

                  return (
                    <div className={styles['result-container-mobile-item']}>
                      {data?.weightOut?.otherIndicators?.[i.subKpiCode] ?? '-'}
                      {weightInValue && weightOutValue && weightOutDiffValues && data?.weightOut?.weightedDate ? (
                        <span
                          className={`${
                            i.indicatorWinningCriteria === 'gain'
                              ? styles['difference-label-alt']
                              : styles['difference-label']
                          } ${
                            weightOutDiffValues > 0
                              ? styles.positive
                              : weightOutDiffValues === 0
                                ? styles.zero
                                : styles.negative
                          }`}
                        >
                          ({weightOutDiffValues > 0 ? '+' : ''}
                          {weightOutDiffValues?.toFixed(1)}
                          {i?.percentageCalculation === 1 ? '%' : ''})
                        </span>
                      ) : null}
                    </div>
                  );
                },
              )}

              {/* <div className={styles['result-container-mobile-item']}>{data?.weightOut?.remarks ?? '-'}</div> */}
            </div>
          </div>
        </>
      )}
      {progressData && data?.campaign && !!data?.campaign?.configJson?.campaignIsProgressMonitoringSectionShown && (
        <>
          <hr className={styles['progress-monitoring-line']} />
          {/* interim measurement  */}
          <div className={styles['progress-monitoring']}>
            <div className={styles['progress-monitoring-title-container']}>
              <div className={styles['progress-monitoring-title-container-title']}>
                {t('dashboard.my-team.participant-profile.label.progressMonitoring')}
              </div>
              <div className={styles['progress-monitoring-title-container-subtitle']}>
                {t('dashboard.my-team.participant-profile.label.optional')}
              </div>
              <Tooltip
                message={t('dashboard.my-team.participant-profile.label.progressMonitoringDesc')}
                preference={TooltipPreference.RIGHT}
                className={styles['input-tool-tip']}
                iconName="circle-exclamation"
              />
            </div>
            {/* <div>Monitor your health stats and see your improvement over time!</div> */}
            <TableContainer
              enableCheckbox={false}
              dataSource={progressData ?? []}
              getColumns={getProgressMonitorColumns}
              pagination={true}
              rowKey={'recId'}
            />
          </div>
        </>
      )}

      {isOpen && (
        <Modal
          primaryButtonLabel={''}
          headline={''}
          closeIcon={false}
          containerWidth={'800px'}
          primaryButton={
            <Button
              title={t('dashboard.my-team.participant-profile.button.save')}
              type={ButtonTypes.BUTTON}
              onClick={() => {
                handleFileUpload();
              }}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.SOLID}
              theme={Theme.GREEN}
              data-testid="test-add-button-popup"
            />
          }
          secondaryButton={
            <Button
              title={t('dashboard.my-team.participant-profile.button.cancel')}
              type={ButtonTypes.BUTTON}
              onClick={handleCloseModal}
              size={ButtonSize.SMALL}
              buttonStyle={ButtonStyles.OUTLINE}
              theme={Theme.GREEN}
              data-testid="test-cancel-button-popup-delete-modal"
            />
          }
        >
          <div className={styles['modal-upload']}>
            {carouselType === 'before' && (
              <div className={styles['before-container']}>
                <div className={styles['title']}>{t('dashboard.my-team.participant-profile.label.before')}</div>
                <div className={styles['upload-container']}>
                  <div className={styles['image-container']}>
                    {images.before_front.url ? (
                      <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                        <img
                          src={images.before_front.url}
                          alt={t('dashboard.my-team.participant-profile.label.beforeFrontViewImageAlt')}
                          className={styles['uploaded-image']}
                        />
                        <div className={styles['buttons-container']}>
                          <button
                            className={styles['change-photo']}
                            onClick={() => document.getElementById('before_front')?.click()}
                          >
                            <img src={ChangeButton} alt="bg" />
                            <input
                              type="file"
                              id="before_front"
                              accept="image/*"
                              onChange={e => handleFile(e, 'before_front', true)}
                              style={{ display: 'none' }}
                            />
                          </button>
                          <button
                            className={styles['remove-photo']}
                            onClick={() => handleRemoveImage('before', 'front')}
                          >
                            <img src={RemoveButton} alt="bg" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ImageEditor onFileUpload={e => handleFileImage(e, 'front', true)} accept="image/*" />
                    )}
                    <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.frontView')}</div>
                  </div>
                  <div className={styles['image-container']}>
                    {images.before_side.url ? (
                      <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                        <img
                          src={images.before_side.url}
                          alt={t('dashboard.my-team.participant-profile.label.beforeSideViewImageAlt')}
                          className={styles['uploaded-image']}
                        />
                        <div className={styles['buttons-container']}>
                          <button
                            className={styles['change-photo']}
                            onClick={() => document.getElementById('before_side')?.click()}
                          >
                            <img src={ChangeButton} alt="bg" />
                            <input
                              type="file"
                              id="before_side"
                              accept="image/*"
                              onChange={e => handleFile(e, 'before_side', true)}
                              style={{ display: 'none' }}
                            />
                          </button>
                          <button
                            className={styles['remove-photo']}
                            onClick={() => handleRemoveImage('before', 'side')}
                          >
                            <img src={RemoveButton} alt="bg" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ImageEditor onFileUpload={e => handleFileImage(e, 'side', true)} accept="image/*" />
                    )}
                    <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.sideView')}</div>
                  </div>
                </div>
              </div>
            )}
            {carouselType === 'after' && (
              <div className={styles['before-container']}>
                <div className={styles['title']}>{t('dashboard.my-team.participant-profile.label.after')}</div>
                <div className={styles['upload-container']}>
                  <div className={styles['image-container']}>
                    {images.after_front.url ? (
                      <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                        <img
                          src={images.after_front.url}
                          alt={t('dashboard.my-team.participant-profile.label.afterFrontViewImageAlt')}
                          className={styles['uploaded-image']}
                        />
                        <div className={styles['buttons-container']}>
                          <button
                            className={styles['change-photo']}
                            onClick={() => document.getElementById('after_front')?.click()}
                          >
                            <img src={ChangeButton} alt="bg" />
                            <input
                              type="file"
                              id="after_front"
                              accept="image/*"
                              onChange={e => handleFile(e, 'after_front', false)}
                              style={{ display: 'none' }}
                            />
                          </button>
                          <button
                            className={styles['remove-photo']}
                            onClick={() => handleRemoveImage('after', 'front')}
                          >
                            <img src={RemoveButton} alt="bg" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ImageEditor onFileUpload={e => handleFileImage(e, 'front', false)} accept="image/*" />
                    )}
                    <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.frontView')}</div>
                  </div>
                  <div className={styles['image-container']}>
                    {images.after_side.url ? (
                      <div className={styles['image-wrapper']} style={{ position: 'relative' }}>
                        <img
                          src={images.after_side.url}
                          alt={t('dashboard.my-team.participant-profile.label.afterSideViewImageAlt')}
                          className={styles['uploaded-image']}
                        />
                        <div className={styles['buttons-container']}>
                          <button
                            className={styles['change-photo']}
                            onClick={() => document.getElementById('after_side')?.click()}
                          >
                            <img src={ChangeButton} alt="bg" />
                            <input
                              type="file"
                              id="after_side"
                              accept="image/*"
                              onChange={e => handleFile(e, 'after_side', false)}
                              style={{ display: 'none' }}
                            />
                          </button>
                          <button className={styles['remove-photo']} onClick={() => handleRemoveImage('after', 'side')}>
                            <img src={RemoveButton} alt="bg" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      <ImageEditor onFileUpload={e => handleFileImage(e, 'side', false)} accept="image/*" />
                    )}
                    <div className={styles['label']}>{t('dashboard.my-team.participant-profile.label.sideView')}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Card>
  );

  //#endregion
  const rewardGiftModal = data?.rewards?.map((r, index) => (
    <div className={styles['reward-container']}>
      <div>
        {r.imageURL ? (
          <img className={styles['reward-container-img']} src={r.imageURL} alt={'reward'} />
        ) : (
          <div className={styles['reward-container-img']}>
            <FAIcon
              iconStyle={IconStyle.SOLID}
              iconName={'gift'}
              theme={Theme.BLACK}
              className={styles['reward-container-icon']}
            />
          </div>
        )}
      </div>
      <div className={styles['reward-container-wrapper']}>
        <div>
          <div className={styles['reward-container-title']}>
            {t('dashboard.my-team.participant-profile.label.yourChosenReward')}
          </div>
          <div className={styles['reward-container-subtitle']}>{r.rewardName}</div>
        </div>
        <div className={styles['reward-container-status-wrapper']}>
          <div className={styles['reward-container-status-title']}>
            {t('dashboard.my-team.participant-profile.label.status')}{' '}
          </div>
          <div className={styles['reward-container-status-tag']}>
            {r.rewardStatus && r.rewardStatus.charAt(0).toUpperCase() + r.rewardStatus.slice(1)}{' '}
            {formatDateDMYYYY(new Date(convertGMT8date(r.modifiedDate)))}
          </div>
        </div>
      </div>
    </div>
  ));

  //#region Reward
  const rewardDetail = (
    <Card
      title={t('dashboard.my-progress.rewardStatusTitle')}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      containerHeight="100%"
    >
      <div className={styles['outer-reward-container']}>
        <div>{rewardGiftModal}</div>
      </div>
    </Card>
  );
  //#endregion

  //#region Participant Progress
  const participantProgress = (
    <Card
      title={t('dashboard.my-team.participant-profile.label.individualProgress')}
      headerStyle={isDesktopView ? HeaderStyle.HEADER_5 : HeaderStyle.HEADER_6}
      containerHeight="100%"
    >
      <div className={styles['progress']}>
        <div className={styles['team-details-container']}>
          <div className={styles['team-details-title-container']}>
            <div className={styles['team-details-coach']}>
              <Tag text={`${data?.campaign?.campaignName}`} theme={Theme.GRAY} style={TagStyle.SOLID} />
            </div>
            <div className={styles['team-details-coach']}>
              {data?.team?.teamStatus && (
                <Tag
                  text={data?.team?.teamStatus ?? ''}
                  theme={data?.team?.teamStatus === 'ACTIVE' ? Theme.SUCCESS : Theme.PURPLE}
                  style={TagStyle.OUTLINE}
                />
              )}
            </div>

            {/* {data?.teamCoachId && (
              <div className={styles['team-details-coach']}>
                <div className={styles['team-details-coach-subtitle']}>{'Team Leader'}</div>
                <Tag
                  text={`${data?.teamCoachId} - ${data?.teamCoachName}`}
                  theme={Theme.WHITE}
                  style={TagStyle.OUTLINE}
                />
              </div>
            )} */}
          </div>
          <div className={styles['']}>
            <div className={styles['team-details-coach']}>
              {`${t('dashboard.my-team.participant-profile.label.teamName')}:`}
              <div className={styles['team-details-date-range']}> {data?.team?.teamName}</div>
            </div>
            <div className={styles['team-details-coach']}>
              {`${t('dashboard.my-team.participant-profile.label.teamDate')}:`}
              <div className={styles['team-details-date-range']}>{`${
                data?.team?.teamStartDate
                  ? formatDateDMYYYY(new Date(data?.team?.teamStartDate))
                  : t('dashboard.my-team.participant-profile.label.notAvailable')
              } - ${
                data?.team?.teamEndDate
                  ? formatDateDMYYYY(new Date(data?.team?.teamEndDate))
                  : t('dashboard.my-team.participant-profile.label.notAvailable')
              }`}</div>
            </div>
          </div>
          <div>
            <div className={styles['team-details-progress-card']}>
              {data && (
                <DetailCardProgress
                  {...data}
                  isCoachEnrollment={props.role !== 'PARTICIPANT' && data.subscribe.length === 0}
                  showFiveProgress={props.role !== 'PARTICIPANT'}
                  purchase={data?.purchase}
                  subscribe={data?.subscribe.map((s: DynamicObjectInterface) => s.orderNumber || '') || []}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
  //#endregion

  return (
    <div className={styles['team-profile']}>
      <div className={styles['team-profile-header']}></div>
      <div className={styles['team-profile-top']}>
        <div>{userDetails}</div>
        <div>{participantProgress}</div>
        {activityNames?.length > 0 && <div>{rewardDetail}</div>}
        <div className={styles['team-profile-bottom']}>{measurementDetail}</div>
      </div>
      {uploadImageModal}
    </div>
  );
};
